/**
 * First-party Beta analytics helper.
 *
 * Fires authenticated POST /api/user-events. No third-party analytics vendor
 * involved — events land inside our own audit chain via the v3 envelope and
 * tenant_id HMAC (per Master BRD Beta v1.0 §13 + Brand Promises 1, 2, 3).
 *
 * Hard rules enforced server-side (mirror them here so we fail fast):
 *   - event_name is a closed enum (3 values at Beta)
 *   - properties: ≤6 keys, string-only, ≤32-char keys, ≤64-char values
 *
 * Never throws — telemetry must never break the UI.
 */

import { getToken } from './authClient';

const API = process.env.REACT_APP_BACKEND_URL;

const ALLOWED_EVENTS = new Set([
  'accuracy_control_toggle',
  'view_revenue_share',
  'demo_excel_download',
  // SPD v5.71 (2026-07-11) — mirror the extended backend enum. Keep in sync
  // with /app/backend/v3/schemas/user_event.py VALID_USER_EVENTS.
  'comparison_evidence_open',
  'comparison_source_library_open',
  'atlas_visit',
  'page_view',
  'query_submitted',
]);

export const trackEvent = (event_name, properties) => {
  try {
    if (!ALLOWED_EVENTS.has(event_name)) return;
    // SPD v5.71-fix (2026-02-11) — single source of truth for the JWT key.
    // Previously read `localStorage.getItem('token')` which no longer matches
    // the real storage key (`startech_token`), so every trackEvent call
    // silently no-op'd for authenticated users. Iteration 64 caught it.
    const token = getToken();
    if (!token || !API) return;

    // Truncate/coerce properties defensively. Server will reject anything
    // out-of-spec with a 400 but we shouldn't ever send that.
    const props = {};
    if (properties && typeof properties === 'object') {
      const entries = Object.entries(properties).slice(0, 6);
      for (const [k, v] of entries) {
        if (typeof k !== 'string' || typeof v !== 'string') continue;
        props[k.slice(0, 32)] = v.slice(0, 64);
      }
    }

    // Fire-and-forget. No await — UI never waits on analytics.
    fetch(`${API}/api/user-events`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ event_name, properties: props }),
      // keepalive lets the request survive page navigation (Exit button etc.)
      keepalive: true,
    }).catch(() => { /* swallowed — never break UI */ });
  } catch (_) {
    // Belt-and-braces — outermost guard.
  }
};

export default trackEvent;
